import { __decorate, __metadata } from "tslib";
import { Component, Mixins, Prop } from "vue-property-decorator";
import { setInteractionMode } from "vee-validate";
import Switches from "vue-switches";
import { Guid } from "guid-typescript";
import BaseMixer from "@/router/views/BaseMixer";
import UnsavedChangesMixin from "@/router/views/UnsavedChangesMixin";
import { toaster } from "@/utilities/toaster";
import { checkImageFile } from "@/utilities/imageFileCheck";
import { ClientContactModel } from "@/store/models/client/clientContact";
import ClientModule from "@/store/modules/client.module";
import { isIndia, RECHECK_MODE } from "@/types/validators";
setInteractionMode("passive");
/**
 * Add Client Contact — inline form rendered inside ClientContactsTabComponent
 * while the shared ClientOverviewMachine is in loadedClientContactsInfo.contactAndTeamInfo.addContactInfo.
 */
let AddClientContactComponent = /** @class */ (() => {
    var _a;
    let AddClientContactComponent = class AddClientContactComponent extends Mixins(BaseMixer, UnsavedChangesMixin) {
        constructor() {
            super(...arguments);
            this.recheckMode = RECHECK_MODE;
            this.clientContact = new ClientContactModel();
            this.previewImage = null;
            // The picked photo stays local (preview only) until Save uploads it
            this.pendingPhoto = null;
            this.uploadedPhotoFile = null;
            this.uploadedPhotoId = "";
            this.uploadingPhoto = false;
        }
        saveClientContact() {
            if (this.uploadingPhoto) {
                return;
            }
            this.validate("newClientContactObserver").then((res) => {
                if (res) {
                    this.uploadPendingPhoto().then((uploaded) => {
                        if (!uploaded) {
                            return;
                        }
                        this.clientContact.clientId = this.clientId.toString();
                        if (this.pendingPhoto) {
                            this.clientContact.mediaFileId = this.uploadedPhotoId;
                        }
                        this.machineService.send({
                            type: "ADDING_CONTACT",
                            payLoad: this.clientContact,
                        });
                    });
                }
            });
        }
        // Uploads the picked photo (if any) and keeps its id; false when the upload failed and the contact must not be saved.
        // A photo already uploaded by an earlier, failed save isn't uploaded again.
        uploadPendingPhoto() {
            if (!this.pendingPhoto || (this.pendingPhoto === this.uploadedPhotoFile && this.uploadedPhotoId)) {
                return Promise.resolve(true);
            }
            const photo = this.pendingPhoto;
            const formData = new FormData();
            formData.append("file", photo);
            this.uploadingPhoto = true;
            return ClientModule.UploadClientPicture({ file: formData, type: "contacts" })
                .then((res) => {
                if (res) {
                    this.uploadedPhotoFile = photo;
                    this.uploadedPhotoId = res.mediaFileId;
                    return true;
                }
                toaster.ShowError("Client Contact image upload failed.");
                return false;
            }, () => false)
                .finally(() => {
                this.uploadingPhoto = false;
            });
        }
        cancelContact() {
            this.confirmLeave().then((leave) => {
                if (leave) {
                    this.machineService.send({ type: "CLOSE_MODAL" });
                }
            });
        }
        // The values that count as unsaved edits (see UnsavedChangesMixin)
        unsavedState() {
            return {
                contact: this.clientContact,
                photo: this.pendingPhoto ? `${this.pendingPhoto.name}:${this.pendingPhoto.size}` : null,
            };
        }
        // Checks the file's content and keeps it for Save; nothing is uploaded yet
        fileSelected(e) {
            const input = e.target;
            const newFiles = input.files || e.dataTransfer.files;
            if (!newFiles.length)
                return;
            const file = newFiles[0];
            checkImageFile(file).then((error) => {
                if (error) {
                    // Clear the input so picking the same file again re-runs the check
                    input.value = "";
                    toaster.ShowError(error);
                    return;
                }
                this.pendingPhoto = file;
                const reader = new FileReader();
                reader.onload = () => {
                    this.previewImage = reader.result;
                };
                reader.readAsDataURL(file);
            });
        }
        // Computed
        get countryLists() {
            return ClientModule.countryLists;
        }
        get selectedCountryName() {
            const countryId = this.clientContact.contactAddress ? this.clientContact.contactAddress.countryId : null;
            const country = this.countryLists.find((x) => x.id.toString() === String(countryId));
            return country ? country.value : "";
        }
        get zipLabel() {
            return isIndia(this.selectedCountryName) ? "PIN Code" : "ZIP Code";
        }
        get stateLists() {
            if (this.clientContact.contactAddress != null && this.clientContact.contactAddress.countryId != null) {
                return ClientModule.stateLists.filter((x) => x.countryId == this.clientContact.contactAddress.countryId);
            }
            return [];
        }
        //Lifecycle
        created() {
            ClientModule.ClearMediaFileData();
        }
    };
    __decorate([
        Prop(),
        __metadata("design:type", typeof (_a = typeof Guid !== "undefined" && Guid) === "function" ? _a : Object)
    ], AddClientContactComponent.prototype, "clientId", void 0);
    __decorate([
        Prop(),
        __metadata("design:type", Object)
    ], AddClientContactComponent.prototype, "machineService", void 0);
    AddClientContactComponent = __decorate([
        Component({
            name: "AddClientContactComponent",
            components: {
                Switches,
            },
        })
    ], AddClientContactComponent);
    return AddClientContactComponent;
})();
export default AddClientContactComponent;
